var { useMemo } = require('react');

/**
 * Cubic bezier formula: B(t) = (1-t)³P0 + 3(1-t)²tP1 + 3(1-t)t²P2 + t³P3
 * @param {number} t - Progress from 0 to 1
 * @param {number} p0 - Start point coordinate
 * @param {number} p1 - First control point coordinate
 * @param {number} p2 - Second control point coordinate
 * @param {number} p3 - End point coordinate
 * @returns {number} - Coordinate at time t
 */
function cubicBezier(t, p0, p1, p2, p3) {
  var oneMinusT = 1 - t;
  return (
    oneMinusT * oneMinusT * oneMinusT * p0 +
    3 * oneMinusT * oneMinusT * t * p1 +
    3 * oneMinusT * t * t * p2 +
    t * t * t * p3
  );
}

/**
 * Get a point on the bezier curve at time t
 * @param {Object} path - Bezier path with start, control1, control2, end points
 * @param {number} t - Progress from 0 to 1
 * @returns {Object} - {x, y} coordinates
 */
function getPointOnPath(path, t) {
  return {
    x: cubicBezier(
      t,
      path.start.x,
      path.control1.x,
      path.control2.x,
      path.end.x,
    ),
    y: cubicBezier(
      t,
      path.start.y,
      path.control1.y,
      path.control2.y,
      path.end.y,
    ),
  };
}

/**
 * Calculate the angle/direction of the curve at time t (for unicorn rotation)
 * @param {Object} path - Bezier path
 * @param {number} t - Progress from 0 to 1
 * @returns {number} - Angle in degrees
 */
function getAngleOnPath(path, t) {
  var delta = 0.001;
  var t1 = Math.max(0, t - delta);
  var t2 = Math.min(1, t + delta);

  var p1 = getPointOnPath(path, t1);
  var p2 = getPointOnPath(path, t2);

  var angle = Math.atan2(p2.y - p1.y, p2.x - p1.x);
  return (angle * 180) / Math.PI;
}

/**
 * Screen edges for starting/ending points
 */
var EDGES = ['top', 'right', 'bottom', 'left'];

/**
 * Get a random point on a screen edge
 * @param {string} edge - 'top', 'right', 'bottom', or 'left'
 * @param {number} width - Screen width
 * @param {number} height - Screen height
 * @param {number} padding - Padding from corners
 * @returns {Object} - {x, y} coordinates
 */
function getPointOnEdge(edge, width, height, padding) {
  if (padding === undefined) padding = 50;

  switch (edge) {
    case 'top':
      return { x: padding + Math.random() * (width - 2 * padding), y: -50 };
    case 'right':
      return {
        x: width + 50,
        y: padding + Math.random() * (height - 2 * padding),
      };
    case 'bottom':
      return {
        x: padding + Math.random() * (width - 2 * padding),
        y: height + 50,
      };
    case 'left':
      return { x: -50, y: padding + Math.random() * (height - 2 * padding) };
    default:
      return { x: -50, y: height / 2 };
  }
}

/**
 * Generate a random bezier path across the screen
 * @param {number} width - Screen width
 * @param {number} height - Screen height
 * @returns {Object} - Bezier path object
 */
function generateRandomPath(width, height) {
  // Pick random start and end edges (must be different)
  var startEdgeIndex = Math.floor(Math.random() * 4);
  var endEdgeIndex = Math.floor(Math.random() * 4);
  while (endEdgeIndex === startEdgeIndex) {
    endEdgeIndex = Math.floor(Math.random() * 4);
  }

  var startEdge = EDGES[startEdgeIndex];
  var endEdge = EDGES[endEdgeIndex];

  var start = getPointOnEdge(startEdge, width, height);
  var end = getPointOnEdge(endEdge, width, height);

  // Control points should create a nice curve through the screen
  // Place them somewhere in the middle-ish of the screen with some randomness
  var control1 = {
    x: width * 0.2 + Math.random() * width * 0.3,
    y: height * 0.2 + Math.random() * height * 0.3,
  };

  var control2 = {
    x: width * 0.5 + Math.random() * width * 0.3,
    y: height * 0.5 + Math.random() * height * 0.3,
  };

  return { start: start, control1: control1, control2: control2, end: end };
}

/**
 * Generate multiple points along the path for the rainbow trail
 * @param {Object} path - Bezier path
 * @param {number} currentT - Current progress (0-1)
 * @param {number} trailLength - How many points to include in trail
 * @returns {Array} - Array of {x, y} points
 */
function getTrailPoints(path, currentT, trailLength) {
  if (trailLength === undefined) trailLength = 20;

  var points = [];
  var step = 0.05; // Each step represents a portion of the trail

  for (var i = 0; i < trailLength; i++) {
    var t = currentT - i * step;
    if (t >= 0) {
      points.push(getPointOnPath(path, t));
    }
  }

  return points;
}

/**
 * Custom hook for bezier path utilities
 * @param {number} width - Screen width
 * @param {number} height - Screen height
 * @returns {Object} - Path utilities
 */
function useBezierPath(width, height) {
  var generatePath = useMemo(
    function () {
      return function () {
        return generateRandomPath(width, height);
      };
    },
    [width, height],
  );

  return {
    generatePath: generatePath,
    getPointOnPath: getPointOnPath,
    getAngleOnPath: getAngleOnPath,
    getTrailPoints: getTrailPoints,
  };
}

module.exports = {
  cubicBezier: cubicBezier,
  getPointOnPath: getPointOnPath,
  getAngleOnPath: getAngleOnPath,
  generateRandomPath: generateRandomPath,
  getTrailPoints: getTrailPoints,
  useBezierPath: useBezierPath,
};
